import axios from "axios";
import {
  Trail,
  HikingTrail,
  MantrailingTrail,
  MantrailingTrailPayload,
  DetectionTrail,
  DetectionTrailPayload,
  CanicrossTrail,
  CanicrossTrailPayload,
} from "../types/trail";
import { ActivityType } from "../types/activityConfig";
import { Dog, Session, PopulatedTrainer, User, RegisterData, LoginCredentials } from "../types";
import { HomepageOverview, StatisticsOverview } from "../types/overview";

export interface SessionDashboard {
  _id: string;
  title: string;
  ownerId: string;
  trainerIds: PopulatedTrainer[];
  createdAt: string;
  updatedAt: string;
  description?: string;
  isPublic?: boolean;
  color?: string;
  icon?: string;
  lastSyncedAt?: string;
}

export interface SessionTemplate {
  startTime: string;
  endTime: string;
  location?: string;
  assignedTrainers?: string[];
  activityType?: ActivityType;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const getHomepageOverview = async (): Promise<HomepageOverview> => {
  const response = await api.get<HomepageOverview>("/api/homepage");
  return response.data;
};

export const getStatisticsOverview = async (): Promise<StatisticsOverview> => {
  const response = await api.get<StatisticsOverview>("/api/statistics");
  return response.data;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  console.log(
    "Axios Interceptor: Token from localStorage",
    token ? "Present" : "Missing"
  );
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Hike API
export const createHike = async (hikeData: HikingTrail) => {
  const response = await api.post("/api/hike", hikeData);
  return response.data;
};

export const getHikes = async (): Promise<HikingTrail[]> => {
  const response = await api.get<HikingTrail[]>("/api/hike/");
  return response.data;
};

export const getHikeById = async (id: string): Promise<HikingTrail> => {
  const response = await api.get<HikingTrail>(`/api/hike/${id}`);
  return response.data;
};

export const updateHike = async (
  id: string,
  hikeData: Partial<HikingTrail>
) => {
  const payload = { ...hikeData };
  if (payload.userTrack === undefined) payload.userTrack = null;
  if (payload.dogTrack === undefined) payload.dogTrack = null;

  const response = await api.put(`/api/hike/${id}`, payload);
  return response.data;
};

export const deleteHike = async (id: string) => {
  const response = await api.delete(`/api/hike/${id}`);
  return response.data;
};

// Trail API
export const getAllTrail = async (): Promise<Trail[]> => {
  const trailResponse = (await api.get<MantrailingTrail[]>("/api/mantrailing/"))
    .data;
  const hikeResponse = await getHikes();
  const detectionResponse = (await api.get<DetectionTrail[]>("/api/detection/")).data;
  const canicrossResponse = (await api.get<CanicrossTrail[]>('/api/canicross/')).data;
  const mantrailingTrails = trailResponse.map((t) => ({
    ...t,
    category: "mantrailing" as const,
  }));
  const hikingTrails = hikeResponse.map((h) => ({
    ...h,
    category: "hiking" as const,
  }));
  const detectionTrails = detectionResponse.map((d) => ({
    ...d,
    category: "detection" as const,
  }));
  const canicrossTrails = canicrossResponse.map((c) => ({
    ...c,
    location: typeof c.location === "string" ? c.location : "",
    locationCoordinate: (c as any).location?.coordinates
      ? [(c as any).location.coordinates[1], (c as any).location.coordinates[0]] as [number, number]
      : undefined,
    category: "canicross" as const,
  }));
  const response = [...mantrailingTrails, ...hikingTrails, ...detectionTrails, ...canicrossTrails];
  return response;
};

export const getTrails = async (): Promise<Trail[]> => {
  const trailResponse = (await api.get<MantrailingTrail[]>("/api/mantrailing/"))
    .data;
  return trailResponse;
};

export const saveTrail = async (trail: Trail | MantrailingTrailPayload) => {
  const response = await api.post("/api/mantrailing/save", trail);
  return response.data;
};

export const deleteTrail = async (id: string) => {
  const response = await api.post(`/api/mantrailing/delete`, { id });
  return response.data;
};

export const updateTrail = async (
  id: string,
  trail: Trail | MantrailingTrailPayload
) => {
  const payload = { ...trail };
  if (isMantrailingTrail(payload)) {
    if (payload.dogTrace === undefined) payload.dogTrace = null;
    if (payload.runnerTrace === undefined) payload.runnerTrace = null;
  }
  const response = await api.post("/api/mantrailing/update", {
    id,
    trail: payload,
  });
  return response.data;
};

// Detection API
export const saveDetection = async (detectionData: DetectionTrailPayload) => {
    const response = await api.post("/api/detection", detectionData);
    return response.data;
};

export const updateDetection = async (id: string, detectionData: Partial<DetectionTrailPayload>) => {
    const response = await api.put(`/api/detection/${id}`, detectionData);
    return response.data;
};

export const deleteDetection = async (id: string) => {
    const response = await api.delete(`/api/detection/${id}`);
    return response.data;
};

export const getCanicross = async (): Promise<CanicrossTrail[]> => {
  const response = await api.get<CanicrossTrail[]>('/api/canicross/');
  return response.data;
};

export const saveCanicross = async (data: CanicrossTrailPayload) => {
  const response = await api.post('/api/canicross', data);
  return response.data;
};

export const updateCanicross = async (id: string, data: Partial<CanicrossTrailPayload>) => {
  const response = await api.put(`/api/canicross/${id}`, data);
  return response.data;
};

export const deleteCanicross = async (id: string) => {
  const response = await api.delete(`/api/canicross/${id}`);
  return response.data;
};

function isMantrailingTrail(
  trail: Trail | MantrailingTrailPayload
): trail is MantrailingTrail {
  return trail.category === "mantrailing";
}

// User API
export const login = async (
  credentials: LoginCredentials
): Promise<{ token: string; user: User }> => {
  const response = await api.post("/api/user/login", credentials);
  localStorage.setItem("token", response.data.token);
  return response.data;
};

export const register = async (
  data: RegisterData
): Promise<{ token: string }> => {
  const response = await api.post("/api/user/register", data);
  localStorage.setItem("token", response.data.token);
  return response.data;
};

export const logout = () => {
  localStorage.removeItem("token");
};

export const checkAuth = async (): Promise<User> => {
  const response = await api.get("/api/user/check");
  return response.data;
};

export const getCurrentUser = async (): Promise<User | null> => {
  const token = localStorage.getItem("token");
  if (!token) {
    return null;
  }
  try {
    const user = await checkAuth();
    return user;
  } catch (error) {
    console.error("Failed to get current user:", error);
    return null;
  }
};

export const updateUser = async (
  id: string,
  updates: Partial<User>
): Promise<User> => {
  const response = await api.put<User>(`/api/user/${id}`, updates);
  return response.data;
};

export const uploadProfilePicture = async (formData: FormData) => {
  const response = await api.post("/api/user/picture", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const uploadDogProfilePhoto = async (
  formData: FormData
): Promise<{ url: string }> => {
  const response = await api.post("/api/dogs/upload-profile-photo", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const updateDogPresentation = async (
  id: string,
  formData: FormData
): Promise<Dog> => {
  const response = await api.put<Dog>(
    `/api/dogs/${id}/presentation`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

export const getAllTrainers = async (): Promise<User[]> => {
  const response = await api.get<User[]>("/api/user-management/trainers");
  return response.data;
};

// Dog API
export const createDog = async (
  dogData: Omit<
    Dog,
    | "_id"
    | "createdAt"
    | "legend"
    | "presentation"
    | "profilePhoto"
    | "presentationPhoto"
  >
): Promise<Dog> => {
  const response = await api.post<Dog>("/api/dogs", dogData);
  return response.data;
};

export const getDogs = async (): Promise<Dog[]> => {
  const response = await api.get<Dog[]>("/api/dogs");
  return response.data;
};

export const getDogById = async (id: string): Promise<Dog> => {
  const response = await api.get<Dog>(`/api/dogs/${id}`);
  return response.data;
};

export const updateDog = async (
  id: string,
  updates: Partial<Dog>
): Promise<Dog> => {
  const response = await api.put<Dog>(`/api/dogs/${id}`, updates);
  return response.data;
};

export const deleteDog = async (id: string): Promise<void> => {
  await api.delete(`/api/dogs/${id}`);
};

export const getDogsByTrainer = async (): Promise<Dog[]> => {
  const response = await api.get<Dog[]>("/api/trainer/dogs");
  return response.data;
};

export const generateDogShareLink = async (
  dogId: string,
  activities: ActivityType[],
  expiresInHours: number = 24
): Promise<{ token: string }> => {
  const response = await api.post<{ token: string }>(
    `/api/dogs/${dogId}/share`,
    { activities, expiresInHours }
  );
  return response.data;
};

export const claimDogWithShareToken = async (
  shareToken: string
): Promise<{ message: string; dogId: string }> => {
  const response = await api.post<{ message: string; dogId: string }>(
    "/api/trainer/claim-dog",
    { shareToken }
  );
  return response.data;
};

export const uploadHikePhoto = async (
  id: string,
  photo: File,
): Promise<string[]> => {
  const formData = new FormData();
  formData.append("photos", photo);
  const response = await api.post(`/api/hike/${id}/photos`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const getTrailByIdForTrainer = async (
  trailId: string
): Promise<Trail> => {
  const response = await api.get<Trail>(`/api/trainer/trails/${trailId}`);
  return response.data;
};

/**
 * Fetches all trails for a specific dog for the logged-in trainer.
 * @param dogId The ID of the dog whose trails are to be fetched.
 * @returns A promise that resolves to an array of trails.
 */
export const getTrailsByDogIdForTrainer = async (dogId: string): Promise<Trail[]> => {
  const response = await api.get<Trail[]>(`/api/trainer/dogs/${dogId}/trails`);
  return response.data;
};

// --- Session Dashboard Endpoints ---

export const getSessionDashboards = async (): Promise<SessionDashboard[]> => {
  const response = await api.get("/api/session-dashboards");
  return response.data;
};

export const createSessionDashboard = async (dashboardData: { title: string; description?: string; isPublic?: boolean; trainerIds?: string[] }): Promise<SessionDashboard> => {
  const response = await api.post("/api/session-dashboards", dashboardData);
  return response.data;
};

export const updateSessionDashboard = async (dashboardId: string, dashboardData: { title: string; description?: string; color?: string; icon?: string; }): Promise<SessionDashboard> => {
  const response = await api.put(`/api/session-dashboards/${dashboardId}`, dashboardData);
  return response.data;
};

export const getSessionDashboardById = async (dashboardId: string): Promise<SessionDashboard> => {
  const response = await api.get(`/api/session-dashboards/${dashboardId}`);
  return response.data;
};

export const generateDashboardInviteCode = async (dashboardId: string): Promise<{ inviteCode: string; expiresAt: string }> => {
  const response = await api.post(`/api/session-dashboards/${dashboardId}/generate-invite`);
  return response.data;
};

export const acceptDashboardInvite = async (inviteCode: string): Promise<SessionDashboard> => {
  const response = await api.post("/api/session-dashboards/accept-invite", { inviteCode });
  return response.data;
};

export const updateSharePermission = async (dashboardId: string, userId: string, permission: 'viewer' | 'editor'): Promise<SessionDashboard> => {
  const response = await api.put(`/api/session-dashboards/${dashboardId}/share/${userId}`, { permission });
  return response.data;
};

// --- Session Endpoints ---

export const getSessionsByDashboard = async (dashboardId: string): Promise<Session[]> => {
  const response = await api.get(`/api/sessions/by-dashboard/${dashboardId}`);
  return response.data;
};

export const createBatchSessions = async (dashboardId: string, sessionTemplates: SessionTemplate[]): Promise<Session[]> => {
  const response = await api.post("/api/sessions/batch", { dashboardId, sessionTemplates });
  return response.data;
};

export const createSession = async (sessionData: Omit<Session, '_id' | 'plannedDogs' | 'assignedTrainers'> & { plannedDogs: string[], assignedTrainers: string[] }): Promise<Session> => {
  const response = await api.post("/api/sessions", sessionData);
  return response.data;
};

export const updateSession = async (sessionId: string, sessionData: Partial<Omit<Session, '_id' | 'plannedDogs' | 'assignedTrainers'>> & { plannedDogs?: { dog: string; objective: string }[], assignedTrainers?: string[] }): Promise<Session> => {
  const response = await api.put(`/api/sessions/${sessionId}`, sessionData);
  return response.data;
};

export const getSessionById = async (sessionId: string): Promise<Session> => {
  const response = await api.get(`/api/sessions/${sessionId}`);
  return response.data;
};

export const deleteSession = async (sessionId: string): Promise<{ sessionId: string }> => {
  const response = await api.delete(`/api/sessions/${sessionId}`);
  return response.data;
};

// --- Admin API ---

export interface AdminStats {
  totalUsers: number;
  userBreakdown: {
    handlers: number;
    trainers: number;
    admins: number;
  };
  totalDogs: number;
  databaseSize: number;
  r2StorageSize: number;
}

export const getAdminStats = async (): Promise<AdminStats> => {
  const response = await api.get<AdminStats>("/api/admin/stats");
  return response.data;
};

export const getAdminUsers = async (role?: string): Promise<User[]> => {
  const params = role ? { role } : {};
  const response = await api.get<User[]>("/api/admin/users", { params });
  return response.data;
};

export const createAdminUser = async (userData: Partial<User>): Promise<User> => {
  const response = await api.post<User>("/api/admin/users", userData);
  return response.data;
};

export const deleteAdminUser = async (userId: string): Promise<void> => {
  await api.delete(`/api/admin/users/${userId}`);
};

export const getAdminDogs = async (): Promise<Dog[]> => {
  const response = await api.get<Dog[]>("/api/admin/dogs");
  return response.data;
};

// This is a placeholder for the old getSessions function to resolve the error in SessionsPage.
// It should be updated or removed depending on the final implementation.
export const getSessions = async (): Promise<Session[]> => {
  // This needs a proper endpoint, for now, it returns an empty array to avoid breaking SessionsPage.
  return [];
};